import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { patchStation, buyBay, buyApparatus, sellApparatus, getState, finishBayNow, buyStationSpecialtyBay, buyJailCell } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Truck, Plus, PencilSimple, Check, X, Diamond, Hammer, CurrencyDollar } from "@phosphor-icons/react";
import { APPARATUS_COST, FIRE_UNIT_TYPES, POLICE_UNIT_TYPES, UNIT_COLOR, UNIT_SHORT, UNIT_TYPES, bayCost, gemCost } from "@/lib/gameConfig";

const MAX_BAYS_PER_STATION = 10; // mirrors backend config
const BAY_FINISH_NOW_GEMS = 50;  // mirrors backend config
const SELL_REFUND_PCT = 0.5;     // mirrors backend: refund = cost // 2
const SPECIALTY_BAY_COST = 300000; // mirrors backend SPECIALTY_BAY_COST

const SORT_OPTIONS = [
  { key: "bay", label: "Bay #" },
  { key: "type", label: "Type" },
  { key: "name", label: "A → Z" },
];

const TYPE_ORDER = Object.fromEntries(UNIT_TYPES.map((t, i) => [t, i]));

export default function StationDialog({
  open,
  onOpenChange,
  station,
  apparatusState,
  dispatches,
  playerMoney,
  playerGems,
  hasHospital = false,
  onPlayerUpdate,
  readOnly = false,
  ownerName = null,
}) {
  const [name, setName] = useState("");
  const [trucks, setTrucks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState("bay");
  // Which name (if any) is currently being edited inline.
  // "station"  → the station header; a truck.id → that truck row; null → none.
  const [editingId, setEditingId] = useState(null);
  // Truck.id pending sell confirmation (first click arms it; second confirms).
  const [confirmSellId, setConfirmSellId] = useState(null);

  useEffect(() => {
    if (station) {
      setName(station.name);
      setTrucks(station.trucks?.map((t) => ({ ...t })) || []);
      setEditingId(null);
    }
  }, [station]);

  // Display order is derived; underlying `trucks` array preserves bay order so #N stays stable.
  // HEMS units are filtered OUT of the regular bay list — they're shown
  // separately in the Specialty Bay section since they live on a Landing Pad.
  const displayedTrucks = useMemo(() => {
    const withIndex = trucks
      .map((t, i) => ({ truck: t, bayIndex: i }))
      .filter(({ truck }) => truck.type !== "HEMS");
    if (sortBy === "type") {
      withIndex.sort((a, b) => {
        const ta = TYPE_ORDER[a.truck.type] ?? 99;
        const tb = TYPE_ORDER[b.truck.type] ?? 99;
        if (ta !== tb) return ta - tb;
        return (a.truck.name || "").localeCompare(b.truck.name || "");
      });
    } else if (sortBy === "name") {
      withIndex.sort((a, b) => (a.truck.name || "").localeCompare(b.truck.name || ""));
    }
    return withIndex;
  }, [trucks, sortBy]);

  // Helicopters housed at this station — rendered separately in the
  // Specialty Bay section, not the regular bay grid above.
  const hemsUnits = useMemo(
    () => trucks
      .map((t, i) => ({ truck: t, bayIndex: i }))
      .filter(({ truck }) => truck.type === "HEMS"),
    [trucks],
  );

  // Build a per-apparatus live status map from the active dispatches list.
  // Priority: returning > onScene > enroute > available.
  const truckStatus = useMemo(() => {
    const m = {};
    (dispatches || []).forEach((d) => {
      if (!d?.apparatusId) return;
      const existing = m[d.apparatusId];
      let label;
      if (d.returning) label = "RETURNING";
      else if (d.onScene) label = "ON SCENE";
      else label = "EN ROUTE";
      // Priority ordering — don't let a stale returning dispatch override a
      // freshly-dispatched outbound one for the same unit.
      const rank = { RETURNING: 1, "ON SCENE": 2, "EN ROUTE": 3 };
      if (!existing || rank[label] > rank[existing]) {
        m[d.apparatusId] = label;
      }
    });
    return m;
  }, [dispatches]);

  // Auto-refresh game state when ANY pending bay's countdown elapses, so the
  // newly-built bay promotes into the dialog UI without a page reload. We poll
  // 1×/sec while the dialog is open + there are pending bays; once one is due,
  // we hit /game/state (which lazily promotes server-side) and push the fresh
  // station list up to Game.jsx.
  useEffect(() => {
    if (!open || !station) return;
    const pending = station.bays_pending || [];
    if (pending.length === 0) return;
    const id = setInterval(async () => {
      const now = Date.now();
      const anyDue = pending.some((p) => {
        try { return new Date(p.completes_at).getTime() <= now; } catch { return false; }
      });
      if (!anyDue) return;
      try {
        const { state: fresh } = await getState();
        if (fresh) onPlayerUpdate(fresh);
      } catch {
        /* swallow — next interval retries */
      }
    }, 1000);
    return () => clearInterval(id);
  }, [open, station, onPlayerUpdate]);

  // Auto-disarm sell confirm after 4 s (player walked away / lost focus).
  useEffect(() => {
    if (!confirmSellId) return;
    const id = setTimeout(() => setConfirmSellId(null), 4000);
    return () => clearTimeout(id);
  }, [confirmSellId]);

  if (!station) return null;

  const renameTruck = (id, newName) => {
    setTrucks((ts) => ts.map((t) => (t.id === id ? { ...t, name: newName } : t)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const trimmedName = name.trim();
      if (!trimmedName) {
        toast.error("Station name cannot be empty");
        setSaving(false);
        return;
      }
      const cleanedTrucks = trucks.map((t) => ({ id: t.id, name: t.name.trim() || "Engine", type: t.type }));
      const updated = await patchStation(station.id, {
        name: trimmedName.slice(0, 60),
        trucks: cleanedTrucks,
      });
      toast.success("Station updated");
      onPlayerUpdate(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleBuyBay = async (payWith = "money") => {
    setBusy(true);
    try {
      const prevPending = (station.bays_pending || []).length;
      const prevBays = station.bays || 0;
      const updated = await buyBay(station.id, { pay_with: payWith });
      onPlayerUpdate(updated);
      // Detect whether the bay queued for construction (pending grew) or
      // landed instantly (built-bay count grew). Toast accordingly so the
      // player knows their money/gems actually went somewhere.
      const newStation = (updated.stations || []).find((s) => s.id === station.id);
      const newPending = (newStation?.bays_pending || []).length;
      const newBays = newStation?.bays || 0;
      if (newPending > prevPending) {
        const p = newStation.bays_pending[newStation.bays_pending.length - 1];
        const sec = p?.build_seconds || 0;
        toast.success(`Bay construction started — ready in ${formatBuildTime(sec)}`);
      } else if (newBays > prevBays) {
        toast.success(`Added a bay to ${station.name}`);
      } else {
        toast.success(`Bay purchased`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy bay");
    } finally {
      setBusy(false);
    }
  };

  const handleFinishBayNow = async (pendingId) => {
    setBusy(true);
    try {
      const updated = await finishBayNow(station.id, pendingId);
      onPlayerUpdate(updated);
      toast.success(`Bay finished — ready now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish bay");
    } finally {
      setBusy(false);
    }
  };

  const handleBuySpecialtyBay = async (payWith = "money") => {
    setBusy(true);
    try {
      const updated = await buyStationSpecialtyBay(station.id, "Landing Pad", payWith);
      onPlayerUpdate(updated);
      toast.success(`Landing Pad built — HEMS housing unlocked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy specialty bay");
    } finally {
      setBusy(false);
    }
  };

  const handleBuyApparatus = async (type, payWith = "money") => {    setBusy(true);
    try {
      const updated = await buyApparatus(station.id, { type, pay_with: payWith });
      onPlayerUpdate(updated);
      toast.success(`Purchased ${type}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy apparatus");
    } finally {
      setBusy(false);
    }
  };

  const handleSellApparatus = async (truck) => {
    setBusy(true);
    setConfirmSellId(null);
    try {
      const updated = await sellApparatus(station.id, truck.id);
      onPlayerUpdate(updated);
      const refund = Math.floor((APPARATUS_COST[truck.type] || 0) * SELL_REFUND_PCT);
      toast.success(`Sold ${truck.name} — refunded $${refund.toLocaleString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sell unit");
    } finally {
      setBusy(false);
    }
  };

  const filled = trucks.filter((t) => t.type !== "HEMS").length;  // HEMS live on landing pads, not regular bays
  const builtBays = station.bays || filled;
  const pendingBays = station.bays_pending || [];
  const totalBays = builtBays + pendingBays.length;
  const emptyBays = Math.max(0, builtBays - filled);
  const nextBayCost = bayCost(totalBays); // counts pending too — backend matches
  const atMaxBays = totalBays >= MAX_BAYS_PER_STATION;

  // Specialty bays (Landing Pad currently the only type). Stations: max 1.
  // HEMS units occupy landing-pad slots independently of regular bays.
  const specialtyBays = station.specialty_bays || [];
  const landingPads = specialtyBays.filter((s) => s.type === "Landing Pad").length;
  const hemsTrucks = trucks.filter((t) => t.type === "HEMS").length;
  const hasSpecialtyBay = specialtyBays.length > 0;
  const emptyLandingPads = Math.max(0, landingPads - hemsTrucks);

  // Render helper for a single truck row — used by BOTH the regular bay
  // list AND the Specialty Bay section (HEMS lives on the Landing Pad).
  const renderTruckRow = (t, bayIndex) => {
    const liveStatus = truckStatus[t.id];
    const stateBusy = apparatusState[t.id]?.available === false;
    const statusLabel = liveStatus || (stateBusy ? "EN ROUTE" : "AVAILABLE");
    const statusClass =
      statusLabel === "AVAILABLE"
        ? "border-emerald-500/40 text-emerald-400"
        : statusLabel === "ON SCENE"
        ? "border-red-500/50 text-red-400"
        : statusLabel === "RETURNING"
        ? "border-cyan-500/40 text-cyan-300"
        : "border-yellow-500/40 text-yellow-400";
    const isEditing = editingId === t.id;
    return (
      <div key={t.id} className="flex items-center gap-2" data-testid={`apparatus-row-${t.id}`}>
        <span className="mono text-[10px] text-zinc-500 w-6 text-right">#{bayIndex + 1}</span>
        <span
          className="mono text-[9px] uppercase tracking-widest px-1.5 py-1 w-12 text-center"
          style={{ background: UNIT_COLOR[t.type] || "#444", color: "#050505" }}
          title={t.type}
        >
          {UNIT_SHORT[t.type] || "ENG"}
        </span>
        {isEditing ? (
          <>
            <Input
              data-testid={`apparatus-name-input-${t.id}`}
              autoFocus
              value={t.name}
              onChange={(e) => renameTruck(t.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setEditingId(null);
                if (e.key === "Escape") {
                  const orig = station.trucks?.find((o) => o.id === t.id)?.name || t.name;
                  renameTruck(t.id, orig);
                  setEditingId(null);
                }
              }}
              maxLength={40}
              className="rounded-none border-emerald-400/50 bg-black/40 mono tracking-wider h-8 text-sm focus-visible:ring-emerald-500/30 flex-1"
            />
            <button
              type="button"
              data-testid={`apparatus-name-confirm-${t.id}`}
              onClick={() => setEditingId(null)}
              title="Done"
              className="border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 p-1.5"
            >
              <Check size={12} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <span
              className="mono tracking-wider text-sm flex-1 truncate px-2 py-1.5 border border-transparent"
              data-testid={`apparatus-name-display-${t.id}`}
              title={t.name}
            >
              {t.name}
            </span>
            <button
              type="button"
              data-testid={`apparatus-name-edit-btn-${t.id}`}
              onClick={() => setEditingId(t.id)}
              title="Rename apparatus"
              disabled={readOnly}
              className={`transition-colors p-1 ${readOnly ? "hidden" : "text-zinc-500 hover:text-emerald-400"}`}
            >
              <PencilSimple size={12} />
            </button>
            {!readOnly && (() => {
              const refund = Math.floor((APPARATUS_COST[t.type] || 0) * SELL_REFUND_PCT);
              const armed = confirmSellId === t.id;
              const sellable = statusLabel === "AVAILABLE";
              if (!sellable) {
                return (
                  <button
                    type="button"
                    disabled
                    title="Recall this unit before selling"
                    className="text-zinc-700 p-1 cursor-not-allowed"
                    data-testid={`apparatus-sell-btn-${t.id}`}
                  >
                    <CurrencyDollar size={12} />
                  </button>
                );
              }
              return armed ? (
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    data-testid={`apparatus-sell-confirm-${t.id}`}
                    onClick={() => handleSellApparatus(t)}
                    disabled={busy}
                    title={`Confirm sale — refund $${refund.toLocaleString()}`}
                    className="mono text-[9px] uppercase tracking-widest px-1.5 py-1 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                  >
                    Sell ${refund.toLocaleString()}?
                  </button>
                  <button
                    type="button"
                    data-testid={`apparatus-sell-cancel-${t.id}`}
                    onClick={() => setConfirmSellId(null)}
                    title="Cancel"
                    className="border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 p-1"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  data-testid={`apparatus-sell-btn-${t.id}`}
                  onClick={() => setConfirmSellId(t.id)}
                  disabled={busy}
                  title={`Sell ${t.name} for $${refund.toLocaleString()} (½ purchase price)`}
                  className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                >
                  <CurrencyDollar size={12} />
                </button>
              );
            })()}
          </>
        )}
        <span
          data-testid={`apparatus-status-${t.id}`}
          className={`mono text-[9px] uppercase tracking-widest px-1.5 py-1 border w-20 text-center ${statusClass}`}
        >
          {statusLabel}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-[#0F1115] border-white/10 text-white max-w-xl"
        data-testid="station-dialog"
      >
        <DialogHeader className="border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <MapPin size={16} className={readOnly ? "text-cyan-400" : "text-emerald-400"} />
            <DialogTitle className="mono uppercase tracking-widest text-xs text-zinc-300 font-semibold">
              {readOnly ? "Group Station — View Only" : "Station Details"}
            </DialogTitle>
            {readOnly ? (
              <span className="ml-auto mono text-[10px] uppercase tracking-widest text-cyan-300 truncate">
                {ownerName ? `Owner: ${ownerName}` : "Mutual Aid"}
              </span>
            ) : (
              <span className="ml-auto mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-3">
                <span>
                  FUNDS <span className="text-emerald-400">${(playerMoney || 0).toLocaleString()}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Diamond size={10} weight="fill" className="text-cyan-300" />
                  <span className="text-cyan-300">{(playerGems ?? 0).toLocaleString()}</span>
                </span>
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2 pr-3 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">
              Station Name
            </label>
            {editingId === "station" ? (
              <div className="flex items-center gap-1.5">
                <Input
                  data-testid="station-name-input"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditingId(null);
                    if (e.key === "Escape") {
                      setName(station.name);
                      setEditingId(null);
                    }
                  }}
                  maxLength={60}
                  className="rounded-none border-emerald-400/50 bg-black/40 mono uppercase tracking-wider focus-visible:ring-emerald-500/30"
                />
                <button
                  type="button"
                  data-testid="station-name-confirm"
                  onClick={() => setEditingId(null)}
                  title="Done"
                  className="border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 p-2"
                >
                  <Check size={13} weight="bold" />
                </button>
                <button
                  type="button"
                  data-testid="station-name-cancel"
                  onClick={() => {
                    setName(station.name);
                    setEditingId(null);
                  }}
                  title="Cancel"
                  className="border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 p-2"
                >
                  <X size={13} weight="bold" />
                </button>
              </div>
            ) : (
              <div
                className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2"
                data-testid="station-name-display"
              >
                <span
                  className="mono uppercase tracking-wider text-sm flex-1 truncate"
                  title={name}
                >
                  {name}
                </span>
                <button
                  type="button"
                  data-testid="station-name-edit-btn"
                  onClick={() => setEditingId("station")}
                  title="Rename station"
                  disabled={readOnly}
                  className={`transition-colors ${readOnly ? "hidden" : "text-zinc-500 hover:text-emerald-400"}`}
                >
                  <PencilSimple size={13} />
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Truck size={11} /> Bays {filled}/{builtBays} • {emptyBays} empty
                {pendingBays.length > 0 && (
                  <span className="text-amber-300 ml-1">
                    • {pendingBays.length} building
                  </span>
                )}
              </span>
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <Button
                    data-testid="buy-bay-btn"
                    onClick={() => handleBuyBay("money")}
                    disabled={busy || atMaxBays || playerMoney < nextBayCost}
                    title={atMaxBays ? `Max ${MAX_BAYS_PER_STATION} bays reached` : `Buy bay for $${nextBayCost.toLocaleString()}`}
                    size="sm"
                    className="rounded-none h-7 mono uppercase text-[10px] tracking-widest bg-amber-500 hover:bg-amber-400 text-black border border-amber-400"
                  >
                    <Plus size={10} className="mr-1" /> Buy Bay (${nextBayCost.toLocaleString()})
                  </Button>
                  <Button
                    data-testid="buy-bay-gems-btn"
                    onClick={() => handleBuyBay("gems")}
                    disabled={busy || atMaxBays || (playerGems ?? 0) < gemCost(nextBayCost)}
                    title={atMaxBays ? `Max ${MAX_BAYS_PER_STATION} bays reached` : `Buy bay with ${gemCost(nextBayCost)} gems`}
                    size="sm"
                    className="rounded-none h-7 mono uppercase text-[10px] tracking-widest bg-transparent text-cyan-300 hover:bg-cyan-400/10 border border-cyan-400/50"
                  >
                    <Diamond size={10} weight="fill" className="mr-1" /> {gemCost(nextBayCost).toLocaleString()}
                  </Button>
                </div>
              )}
            </div>
            {trucks.length > 1 && (
              <div
                className="flex items-center gap-1 mb-2 flex-wrap"
                data-testid="station-sort-controls"
              >
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mr-1">
                  Sort
                </span>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    data-testid={`station-sort-${opt.key}`}
                    onClick={() => setSortBy(opt.key)}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border transition-colors ${
                      sortBy === opt.key
                        ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
                        : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              {displayedTrucks.map(({ truck: t, bayIndex }) => renderTruckRow(t, bayIndex))}
              {Array.from({ length: emptyBays }).map((_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-2 opacity-70 border border-dashed border-white/10 px-2 py-1.5">
                  <span className="mono text-[10px] text-zinc-500 w-6 text-right">#{filled + i + 1}</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex-1">— EMPTY BAY —</span>
                </div>
              ))}
              {pendingBays.map((p, i) => (
                <PendingBayRow
                  key={p.id || `pending-${i}`}
                  pending={p}
                  bayNumber={builtBays + i + 1}
                  playerGems={playerGems}
                  busy={busy}
                  readOnly={readOnly}
                  onFinishNow={() => handleFinishBayNow(p.id)}
                />
              ))}
            </div>
          </div>

          {/* Specialty Bay panel — max 1 per station; Landing Pad unlocks HEMS housing.
              Hidden on police stations (uses jail cells instead). */}
          <div className={(readOnly || (station.kind || "fire") === "police") ? "hidden" : ""}>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
              Specialty Bay {hasSpecialtyBay && <span className="text-emerald-400">(1/1 built)</span>}
              {!hasSpecialtyBay && <span className="text-zinc-600">(0/1 available)</span>}
            </div>
            {hasSpecialtyBay ? (
              <div className="space-y-1.5">
                <div
                  data-testid="station-specialty-bay-row"
                  className="border-l-2 px-2 py-1 flex items-center gap-2 text-[10px] mono"
                  style={{ borderLeftColor: "#A6F11C", background: "rgba(166,241,28,0.05)" }}
                >
                  <span className="uppercase tracking-widest text-[10px] text-lime-300">Landing Pad</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-300">
                    {hemsTrucks > 0 ? `${hemsTrucks} HEMS housed` : "Empty — ready for HEMS"}
                  </span>
                </div>
                {hemsUnits.map(({ truck, bayIndex }) => renderTruckRow(truck, bayIndex))}
                {emptyLandingPads > 0 && Array.from({ length: emptyLandingPads }).map((_, i) => (
                  <div
                    key={`empty-pad-${i}`}
                    className="flex items-center gap-2 opacity-70 border border-dashed px-2 py-1.5"
                    style={{ borderColor: "rgba(166,241,28,0.25)" }}
                  >
                    <span className="mono text-[10px] text-lime-400/70 w-6 text-right">★</span>
                    <span className="mono text-[10px] uppercase tracking-widest text-lime-300/70 flex-1">
                      — EMPTY LANDING PAD —
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="mono text-[10px] text-zinc-400 flex-1">
                  {hasHospital
                    ? "Landing Pad — required to house a HEMS helicopter."
                    : "Build (or join an alliance with) a hospital before buying a Landing Pad."}
                </span>
                <button
                  type="button"
                  data-testid="buy-specialty-landing-pad"
                  onClick={() => handleBuySpecialtyBay("money")}
                  disabled={busy || !hasHospital || playerMoney < SPECIALTY_BAY_COST}
                  title={
                    !hasHospital
                      ? "Requires at least one hospital (yours or your alliance's)"
                      : `Buy Landing Pad for $${SPECIALTY_BAY_COST.toLocaleString()}`
                  }
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                    busy || !hasHospital || playerMoney < SPECIALTY_BAY_COST
                      ? "border-white/5 text-zinc-600 cursor-not-allowed"
                      : "border-lime-400/50 text-lime-300 hover:bg-lime-400/10"
                  }`}
                >
                  ${SPECIALTY_BAY_COST.toLocaleString()}
                </button>
                <button
                  type="button"
                  data-testid="buy-specialty-landing-pad-gems"
                  onClick={() => handleBuySpecialtyBay("gems")}
                  disabled={busy || !hasHospital || (playerGems ?? 0) < gemCost(SPECIALTY_BAY_COST)}
                  title={
                    !hasHospital
                      ? "Requires at least one hospital (yours or your alliance's)"
                      : `Buy Landing Pad for ${gemCost(SPECIALTY_BAY_COST)} gems`
                  }
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                    busy || !hasHospital || (playerGems ?? 0) < gemCost(SPECIALTY_BAY_COST)
                      ? "border-white/5 text-zinc-600 cursor-not-allowed"
                      : "border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"
                  }`}
                >
                  {gemCost(SPECIALTY_BAY_COST)} GEMS
                </button>
              </div>
            )}
          </div>

          {/* Jail Cells panel — police stations only. Behaves like
              hospital beds; arrestees hold a cell for 1 hour then
              auto-release. Cap: 10 per station. */}
          {(station.kind || "fire") === "police" && (
            <div className={readOnly ? "hidden" : ""}>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                Jail Cells
                <span className="text-blue-300">
                  {station.jail_cells || 0}/10 built
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] mb-2 border border-white/5 bg-white/[0.02] p-2">
                <span className="mono text-[10px] text-zinc-400 flex-1">
                  Capacity {station.jail_cells || 0} cell{(station.jail_cells || 0) === 1 ? "" : "s"} · Arrestees held ~1 hour then auto-released.
                </span>
              </div>
              {(station.jail_cells || 0) < 10 && (
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="mono text-[10px] text-zinc-400 flex-1">
                    Add jail cell (max 10)
                  </span>
                  <button
                    type="button"
                    data-testid="buy-jail-cell-money"
                    onClick={async () => {
                      try {
                        setBusy(true);
                        const updated = await buyJailCell(station.id, "money");
                        onPlayerUpdate(updated);
                        toast.success("Jail cell added");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not add jail cell");
                      } finally { setBusy(false); }
                    }}
                    disabled={busy || playerMoney < 50000}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      busy || playerMoney < 50000
                        ? "border-white/5 text-zinc-600 cursor-not-allowed"
                        : "border-blue-400/50 text-blue-300 hover:bg-blue-400/10"
                    }`}
                  >
                    $50,000
                  </button>
                  <button
                    type="button"
                    data-testid="buy-jail-cell-gems"
                    onClick={async () => {
                      try {
                        setBusy(true);
                        const updated = await buyJailCell(station.id, "gems");
                        onPlayerUpdate(updated);
                        toast.success("Jail cell added");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not add jail cell");
                      } finally { setBusy(false); }
                    }}
                    disabled={busy || (playerGems ?? 0) < gemCost(50000)}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      busy || (playerGems ?? 0) < gemCost(50000)
                        ? "border-white/5 text-zinc-600 cursor-not-allowed"
                        : "border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"
                    }`}
                  >
                    {gemCost(50000)} GEMS
                  </button>
                </div>
              )}
            </div>
          )}

          <div className={readOnly ? "hidden" : ""}>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Purchase Apparatus
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(() => {
                // Fire stations show fire/EMS units; police stations show
                // police units. This mirrors the backend gate in
                // buy_apparatus that rejects cross-kind purchases.
                const stationKind = station.kind || "fire";
                const allowed = stationKind === "police" ? POLICE_UNIT_TYPES : FIRE_UNIT_TYPES;
                return UNIT_TYPES.filter((t) => allowed.includes(t));
              })().map((u) => {
                const cost = APPARATUS_COST[u];
                const gCost = gemCost(cost);
                const moneyOk = playerMoney >= cost;
                const gemsOk = (playerGems ?? 0) >= gCost;
                const medicLocked = u === "Medic" && !hasHospital;
                // HEMS needs a Landing Pad specialty bay AND a receiving
                // hospital (own or alliance-owned) to fly patients to.
                const hemsLocked = u === "HEMS" && (!hasHospital || landingPads === 0 || emptyLandingPads === 0);
                const noBaySpace = u === "HEMS" ? emptyLandingPads === 0 : emptyBays === 0;
                const cardDisabled = busy || noBaySpace || medicLocked || hemsLocked;
                const moneyDisabled = cardDisabled || !moneyOk;
                const gemsDisabled = cardDisabled || !gemsOk;
                const visuallyDimmed = cardDisabled || (!moneyOk && !gemsOk);
                return (
                  <div
                    key={u}
                    data-testid={`apparatus-card-${u.replace(/\s+/g, "-")}`}
                    className={`border p-2 transition-colors ${
                      visuallyDimmed
                        ? "border-white/5 text-zinc-600"
                        : "border-white/10 text-white"
                    }`}
                    style={!visuallyDimmed ? { borderLeft: `3px solid ${UNIT_COLOR[u]}` } : { borderLeft: "3px solid rgba(255,255,255,0.05)" }}
                  >
                    <div
                      className="mono text-[10px] uppercase tracking-widest"
                      style={{ color: visuallyDimmed ? "#525866" : UNIT_COLOR[u] }}
                    >
                      {UNIT_SHORT[u]}
                    </div>
                    <div className="text-[12px] font-semibold leading-tight">{u}</div>
                    {medicLocked && (
                      <div className="mono text-[8px] uppercase tracking-widest text-amber-300 mt-0.5">
                        Build a hospital first
                      </div>
                    )}
                    {u === "HEMS" && !hasHospital && (
                      <div className="mono text-[8px] uppercase tracking-widest text-amber-300 mt-0.5">
                        Build a hospital first
                      </div>
                    )}
                    {u === "HEMS" && hasHospital && landingPads === 0 && (
                      <div className="mono text-[8px] uppercase tracking-widest text-amber-300 mt-0.5">
                        Build a Landing Pad first
                      </div>
                    )}
                    {u === "HEMS" && landingPads > 0 && emptyLandingPads === 0 && (
                      <div className="mono text-[8px] uppercase tracking-widest text-amber-300 mt-0.5">
                        Landing Pad occupied
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-1">
                      <button
                        type="button"
                        data-testid={`buy-apparatus-${u.replace(/\s+/g, "-")}`}
                        onClick={() => handleBuyApparatus(u, "money")}
                        disabled={moneyDisabled}
                        title={`Buy ${u} for $${cost.toLocaleString()}`}
                        className={`flex-1 mono text-[10px] uppercase tracking-widest px-1.5 py-1 border ${
                          moneyDisabled
                            ? "border-white/5 text-zinc-600 cursor-not-allowed"
                            : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        }`}
                      >
                        ${cost.toLocaleString()}
                      </button>
                      <button
                        type="button"
                        data-testid={`buy-apparatus-gems-${u.replace(/\s+/g, "-")}`}
                        onClick={() => handleBuyApparatus(u, "gems")}
                        disabled={gemsDisabled}
                        title={`Buy ${u} for ${gCost} gems`}
                        className={`flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-1.5 py-1 border ${
                          gemsDisabled
                            ? "border-white/5 text-zinc-600 cursor-not-allowed"
                            : "border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"
                        }`}
                      >
                        <Diamond size={9} weight="fill" />
                        {gCost.toLocaleString()}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {emptyBays === 0 && (
              <div className="mono text-[10px] uppercase tracking-widest text-yellow-400 mt-2">
                NO EMPTY BAYS — buy a bay first
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-white/10 pt-3 mt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none mono uppercase text-[11px] tracking-widest text-zinc-400 hover:text-white hover:bg-white/5"
            data-testid="station-dialog-close"
          >
            Close
          </Button>
          {!readOnly && (
            <Button
              data-testid="station-dialog-save"
              onClick={handleSave}
              disabled={saving}
              className="rounded-none mono uppercase text-[11px] tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 border border-emerald-400"
            >
              {saving ? "Saving..." : "Save Names"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Format a duration in seconds into a short "Xh Ym" or "Ym Zs" string.
function formatBuildTime(totalSec) {
  const s = Math.max(0, Math.floor(totalSec || 0));
  if (s <= 0) return "ready";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m >= 1) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

// Live-updating row for a bay that is under construction.
// Re-renders every second so the countdown is fluid; promotes itself
// implicitly when the parent's `bays_pending` array no longer contains it
// (the next /game/state refresh clears the row entirely).
function PendingBayRow({ pending, bayNumber, playerGems = 0, busy = false, readOnly = false, onFinishNow }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const completesAt = useMemo(() => {
    try {
      return new Date(pending.completes_at).getTime();
    } catch {
      return Date.now();
    }
  }, [pending.completes_at]);
  const totalMs = (pending.build_seconds || 1) * 1000;
  const remainingMs = Math.max(0, completesAt - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);
  const elapsedPct = Math.min(100, Math.max(0, 100 - (remainingMs / totalMs) * 100));
  const canAfford = (playerGems || 0) >= BAY_FINISH_NOW_GEMS;
  return (
    <div
      data-testid={`pending-bay-${pending.id}`}
      className="flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 px-2 py-1.5"
    >
      <span className="mono text-[10px] text-amber-300 w-6 text-right">#{bayNumber}</span>
      <Hammer size={11} className="text-amber-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="mono text-[10px] uppercase tracking-widest text-amber-300">
          Under Construction
        </div>
        <div className="h-1 bg-white/5 mt-1 overflow-hidden">
          <div
            className="h-full bg-amber-400 transition-[width] duration-700 ease-linear"
            style={{ width: `${elapsedPct}%` }}
          />
        </div>
      </div>
      <span className="mono text-[10px] text-amber-300 w-20 text-right">
        {formatBuildTime(remainingSec)}
      </span>
      <button
        type="button"
        data-testid={`finish-bay-now-${pending.id}`}
        onClick={onFinishNow}
        disabled={readOnly || busy || !canAfford}
        title={
          canAfford
            ? `Finish this bay immediately for ${BAY_FINISH_NOW_GEMS} gems`
            : `Need ${BAY_FINISH_NOW_GEMS} gems`
        }
        className={`flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
          readOnly ? "hidden" : (busy || !canAfford
            ? "border-white/10 text-zinc-600 cursor-not-allowed"
            : "border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10")
        }`}
      >
        <Diamond size={9} weight="fill" />
        {BAY_FINISH_NOW_GEMS} — Finish
      </button>
    </div>
  );
}
